import { Stack } from "@mui/material";
import { useEffect, useMemo } from "react";
import { useAlergyIntoleranceByOrganization } from "@/services/fhir/alergyintolerance/hook/useAlergyIntoleranceByOrganization";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useDict } from "@/utils/useDict";
import type { ClinicalInsightChildProps } from "src-next/modules/clinical-insights/types/analysisTypes";
import {
  buildAllergyInsightCharts,
  buildAllergyPatientRows,
} from "src-next/modules/clinical-insights/transformers/mapAllergyInsights";
import { useFhirInsightLoadPhase } from "src-next/modules/clinical-insights/hooks/useFhirInsightLoadPhase";
import { matchesPractitionerFilter } from "src-next/modules/clinical-insights/helpers/filterResourcesByPractitioner";
import { getInsightAnalysisTexts } from "src-next/modules/clinical-insights/components/workspace/config/analysisInsightConfig";
import { useInsightEmptyLabels } from "src-next/modules/clinical-insights/components/workspace/config/insightEmptyLabels";
import { InsightWorkspace } from "src-next/modules/clinical-insights/components/workspace/shell/InsightWorkspace";

const texts = getInsightAnalysisTexts("alergias");

export function AllergiesAnalysis({
  dateRange,
  practitionerId = "all",
  practitioner,
  accentColor,
  onPatientStatsChange,
  practitionerFilter: practitionerSelectSlot,
  onLoadPhaseChange,
  onSelectAllPractitioners,
  onPreviousPeriod,
  periodDisplayLabel,
  isActive = true,
}: ClinicalInsightChildProps) {
  const { organizationId } = useOrganizationContext();
  const practitionerFilter = practitionerId || "all";
  const dict = useDict;

  const allergyDict = useMemo(
    () => ({
      clinicalStatus: dict.clinicalStatus,
      criticality: dict.criticality,
      category: dict.category,
    }),
    [dict.clinicalStatus, dict.criticality, dict.category]
  );

  const {
    data: allergyData,
    loading: loadingAllergies,
    fetchAlergyIntolerances,
  } = useAlergyIntoleranceByOrganization({
    organizationId: organizationId || "",
    startDate: dateRange?.startDate,
    endDate: dateRange?.endDate,
    includePatient: true,
    includeEncounter: true,
  });

  useEffect(() => {
    if (organizationId && isActive) fetchAlergyIntolerances();
  }, [organizationId, dateRange?.startDate, dateRange?.endDate, isActive, fetchAlergyIntolerances]);

  const { hasData } = useFhirInsightLoadPhase(
    Boolean(organizationId) && isActive,
    loadingAllergies,
    onLoadPhaseChange
  );

  const allergies = useMemo(() => {
    const list = allergyData?.allergyIntolerances || [];
    if (practitionerFilter === "all") return list;
    return list.filter((a) =>
      matchesPractitionerFilter(practitionerFilter, a.recorder?.reference)
    );
  }, [allergyData?.allergyIntolerances, practitionerFilter]);
  const patients = allergyData?.patients || [];

  const filterResetKey = `${practitionerFilter}-${dateRange?.startDate ?? ""}-${dateRange?.endDate ?? ""}`;
  const { emptyPeriodLabel, emptyPractitionerLabel } = useInsightEmptyLabels(
    dateRange,
    practitionerFilter,
    practitioner,
    periodDisplayLabel
  );

  const { statusItems, secondaryItems, rankingItems, total } = useMemo(
    () => buildAllergyInsightCharts(allergies, allergyDict),
    [allergies, allergyDict]
  );

  const patientRows = useMemo(
    () => buildAllergyPatientRows(allergies, patients, allergyDict),
    [allergies, patients, allergyDict]
  );

  const statusLabelByKey = useMemo(() => {
    const map: Record<string, string> = {};
    for (const item of statusItems) map[item.key] = item.label;
    return map;
  }, [statusItems]);

  const secondaryLabelByKey = useMemo(() => {
    const map: Record<string, string> = {};
    for (const item of secondaryItems) map[item.key] = item.label;
    return map;
  }, [secondaryItems]);

  const statusInsightTitle = useMemo(() => {
    if (statusItems.length === 0) return "";
    const sorted = [...statusItems].sort((a, b) => b.value - a.value);
    const percentage = total > 0 ? Math.round((sorted[0].value / total) * 100) : 0;
    return `${percentage}% das ${texts.itemPlural} estão como '${sorted[0].label}'`;
  }, [statusItems, total]);

  const loadPhase = loadingAllergies ? "records" : "done";

  return (
    <Stack width="100%" height="100%" spacing={0} sx={{ minHeight: 0 }}>
      <InsightWorkspace
        accentColor={accentColor ?? "#e91e63"}
        texts={texts}
        practitionerFilter={practitionerSelectSlot}
        filterResetKey={filterResetKey}
        loadPhase={loadPhase}
        patientsProgress={{ loaded: 0, total: 0 }}
        hasData={hasData || allergies.length > 0}
        isEmpty={total === 0 && !loadingAllergies}
        emptyPeriodLabel={emptyPeriodLabel}
        emptyPractitionerLabel={emptyPractitionerLabel}
        showClearPractitionerAction={practitionerFilter !== "all"}
        onClearPractitioner={onSelectAllPractitioners}
        onPreviousPeriod={onPreviousPeriod}
        onRetry={() => fetchAlergyIntolerances()}
        statusChart={{ insightText: statusInsightTitle, items: statusItems }}
        secondaryStatusChart={{
          title: "Criticidade",
          items: secondaryItems,
        }}
        rankingChart={{ insightText: texts.rankingHint, items: rankingItems }}
        patientRows={patientRows}
        statusLabelByKey={{ ...statusLabelByKey, ...secondaryLabelByKey }}
        onPatientStatsChange={onPatientStatsChange}
      />
    </Stack>
  );
}
